import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMiniAppApi } from '../lib/context';
import { useAuthState } from '../lib/auth-context';

interface SummaryHeaderProps {
  tasksCount?: number;
  remindersCount?: number;
}

interface TelegramUserInfo {
  firstName?: string;
  username?: string;
  id?: number;
}

export function SummaryHeader({ tasksCount, remindersCount }: SummaryHeaderProps) {
  const auth = useAuthState();
  const api = useMiniAppApi();
  const userIdentifier = auth.session?.userId ?? auth.session?.telegramUserId;
  const canQuery =
    !!userIdentifier &&
    (auth.status === 'authenticated' ||
      auth.status === 'cached-session' ||
      auth.status === 'read-only');

  const userInfo = useMemo<TelegramUserInfo | null>(() => {
    try {
      const user = window.Telegram?.WebApp?.initDataUnsafe?.user;
      if (user) {
        return {
          firstName: user.first_name ?? undefined,
          username: user.username,
          id: user.id,
        };
      }
    } catch (error) {
      console.warn('[SummaryHeader] Failed to read user info:', error);
    }
    return null;
  }, []);

  const settingsQuery = useQuery({
    queryKey: ['summary-settings', userIdentifier],
    queryFn: () => api.getSettings(userIdentifier || undefined),
    enabled: canQuery,
  });

  const displayName = (() => {
    if (auth.status === 'needs-initdata') {
      return 'Нужна авторизация';
    }
    if (userInfo?.firstName) {
      return userInfo.firstName;
    }
    if (userInfo?.username) {
      return `@${userInfo.username}`;
    }
    const displayId = auth.session?.userId ?? auth.session?.telegramUserId;
    if (displayId) {
      return `ID: ${displayId}`;
    }
    return 'Гость';
  })();

  const statusBadge = (() => {
    switch (auth.status) {
      case 'cached-session':
        return '🔒 Используем сохранённую сессию';
      case 'read-only':
        return '👀 Режим просмотра';
      case 'needs-initdata':
        return '⚠️ Требуется запуск из Telegram';
      case 'error':
        return '⚠️ Ошибка авторизации';
      default:
        return undefined;
    }
  })();

  return (
    <div className="summary-header">
      <div className="summary-header__content">
        <div className="summary-header__user">
          <div className="summary-header__user-name">{displayName}</div>
          <div className="summary-header__user-tz">
            🕒 {settingsQuery.data?.timezone ?? 'UTC'}
          </div>
          {statusBadge && (
            <div className="summary-header__badge">{statusBadge}</div>
          )}
        </div>
        <div className="summary-header__stats">
          <div className="summary-header__stat">
            <span className="summary-header__stat-label">Задачи</span>
            <span className="summary-header__stat-value">
              {typeof tasksCount === 'number' ? tasksCount : '—'}
            </span>
          </div>
          <div className="summary-header__stat">
            <span className="summary-header__stat-label">Напоминания</span>
            <span className="summary-header__stat-value">
              {typeof remindersCount === 'number' ? remindersCount : '—'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

